import { useEffect, useRef, useCallback } from "react";
import maplibregl from "maplibre-gl";
import layerGroups from "layers/layers";
import { LayerGroupName } from "types/dataModel";
import { kFormatter } from "hooks/utils/formattingUtils";
import { BasemapStyle } from "basemap_manager/BasemapManager";
import { useAnalyticsHandler } from "./useAnalytics";

const baseStyles = "transition-opacity w-56 ";

interface ColorDefintion {
  bgColor: string;
  textColor: string;
  borderColor: string;
}

const ThemeMap: Record<BasemapStyle, ColorDefintion> = {
  [BasemapStyle.Satellite]: {
    bgColor: "primary-blue",
    textColor: "white",
    borderColor: "primary-blue",
  },
  [BasemapStyle.Light]: {
    bgColor: "white",
    textColor: "black",
    borderColor: "secondary-lightBlue",
  },
  [BasemapStyle.Dark]: {
    bgColor: "primary-blue",
    textColor: "white",
    borderColor: "primary-blue",
  },
};

export function useHover(
  map: maplibregl.Map,
  selectedLayer: LayerGroupName,
  theme: BasemapStyle,
) {
  const mapScale = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const popover = useRef<maplibregl.Popup | null>(null);
  const hoveredFeature = useRef<maplibregl.MapGeoJSONFeature | null>(null);
  const colors = ThemeMap[theme];
  const analyticsHandler = useAnalyticsHandler("Feature Hovered");

  useEffect(() => {
    mapScale.current = map?.getZoom();
    map?.on("zoomend", () => {
      mapScale.current = map.getZoom();
    });
  }, [map]);

  const updateFeatureState = useCallback(
    (feature: maplibregl.MapGeoJSONFeature, state: boolean) => {
      map.setFeatureState(
        {
          source: feature.layer.source as string,
          sourceLayer: (feature.layer as any)["source-layer"] as string,
          id: feature.id,
        },
        { hovered: state },
      );
    },
    [map],
  );

  const onHover = useCallback(
    (e: maplibregl.MapLayerMouseEvent) => {
      if (
        mapScale.current > 4 &&
        e.features?.[0]?.properties?.[
          layerGroups[selectedLayer]?.metricKey as string
        ]
      ) {
        const hoveredTessela = e.features?.[0];

        // Already hovering this exact feature — nothing to do
        if (hoveredFeature.current?.id === hoveredTessela.id) {
          return;
        }

        map.getCanvas().style.cursor = "pointer";

        // Clear the previous feature's hovered state before setting the new one
        if (hoveredFeature.current) {
          updateFeatureState(hoveredFeature.current, false);
        }

        if (popover.current) {
          popover.current.remove();
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        updateFeatureState(hoveredTessela, true);
        hoveredFeature.current = hoveredTessela;
        popover.current = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        popover.current.setHTML(`
          <div class="rounded border-${
            colors.borderColor
          } border-[1px] p-4 bg-${colors.bgColor}">
            <h6 class="mb-1 text-${
              colors.textColor
            }">Study Unit (${hoveredTessela.properties?.ISO3})</h6>
            ${
              layerGroups[selectedLayer]?.metricKey &&
              hoveredTessela.properties?.[
                layerGroups[selectedLayer]?.metricKey as string
              ]
                ? `<p class="lining-nums text-${
                    colors.textColor
                  }">${selectedLayer}:  ${
                    selectedLayer === LayerGroupName.Population ? "" : "$"
                  }${kFormatter(
                    hoveredTessela.properties[
                      layerGroups[selectedLayer]?.metricKey as string
                    ],
                  )} ${layerGroups[selectedLayer]?.units}</p>`
                : ""
            }
          </div>
        `);

        // Copy coordinates array.
        const coordinates = (hoveredTessela.geometry as any).coordinates as [
          number,
          number,
        ];

        // Get the screen coordinates of the point
        const point = map.project(coordinates);
        const mapHeight = map.getContainer().clientHeight;

        // Determine if the popup should be rendered above or below the point
        const offset = point.y > mapHeight * 0.1 ? -20 : 20;

        // Populate the popup and set its coordinates
        // based on the feature found.
        popover.current
          .setLngLat({
            lng: coordinates[0],
            lat: coordinates[1],
          })
          .setOffset([0, offset])
          .addTo(map);

        setTimeout(() => {
          if (popover.current) {
            timeoutRef.current && clearTimeout(timeoutRef.current);
            [...baseStyles.split(" "), "!opacity-100"].forEach((className) => {
              className.length && popover.current?.addClassName(className);
            });
          }
        }, 100);

        analyticsHandler({
          featureId: hoveredTessela.id,
          layerGroup: selectedLayer,
        });
      }
    },
    [
      analyticsHandler,
      colors.bgColor,
      colors.borderColor,
      colors.textColor,
      map,
      selectedLayer,
      updateFeatureState,
    ],
  );

  const onHoverEnd = useCallback(() => {
    map.getCanvas().style.cursor = "grab";
    if (hoveredFeature.current) {
      updateFeatureState(hoveredFeature.current, false);
    }
    setTimeout(() => {
      if (popover.current) {
        [...baseStyles.split(" "), "!opacity-100"].forEach((className) => {
          className.length && popover.current?.addClassName(className);
        });
        timeoutRef.current = setTimeout(() => {
          if (popover.current) {
            popover.current.remove();
            popover.current = null;
          }
        }, 500);
      }
    }, 50);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [map]);

  useEffect(() => {
    if (!map) return;

    const handleMove = (e: maplibregl.MapLayerMouseEvent) => onHover(e);
    const handleLeave = () => onHoverEnd();

    map.on("mousemove", "tessela_rps", handleMove);
    map.on("mouseleave", "tessela_rps", handleLeave);

    return () => {
      map.off("mousemove", "tessela_rps", handleMove);
      map.off("mouseleave", "tessela_rps", handleLeave);
    };
  }, [map, onHover, onHoverEnd]);
}
